import { DiffStates } from './version';
import { getAgentId } from '@lib/index';

export interface OriginKey {
  agentId: string;
  userId: string;
  commitNumber: string;
  tag?: string;
}

export function originKeyToLocal(key?: any) {
  if (!key) {
    return undefined;
  }

  return {
    agentId: key.agent_id,
    userId: key.user_id,
    commitNumber: key.commit_number,
    tag: key.tag
  };
}

export function originKeyToServer(key?: OriginKey) {
  if (!key) {
    return undefined;
  }

  return {
    agent_id: key.agentId,
    user_id: key.userId,
    commit_number: key.commitNumber
  };
}

export interface BasePageModel {
  id?: string;
  name?: string;
  comment?: string;
  description?: string;
  originKey?: OriginKey;
  structKey?: OriginKey;
  ownerId?: string;
  ownerType?: string;
  inheritedFromCompositeId?: string;
  inheritedFromCompositeName?: string;
  isOverwrite?: boolean;
  agentId?: string;
  frameId?: string;
  state?: DiffStates;
  source?: string;
  qualification?: string;
  localPlus?: boolean;
  toBeDeleted?: boolean;
}

export function basePageToLocal(data: any) {
  return {
    id: data.id,
    name: data.name,
    comment: data.comment,
    description: data.description,
    originKey: data.origin_key && originKeyToLocal(data.origin_key),
    structKey: data.struct_key && originKeyToLocal(data.struct_key),
    ownerId: data.owner_id,
    ownerType: data.owner_type,
    inheritedFromCompositeId: data.inherited_from_composite_id,
    inheritedFromCompositeName: data.inherited_from_composite_name,
    isOverwrite: data.is_overwrite || false,
    agentId: data.agent_id,
    frameId: data.frame_id,
    source: data.source,
    qualification: data.qualification,
    localPlus: data.local_plus,
    state: data.state
  };
}

export function basePageToServer(data: BasePageModel) {
  return {
    id: data.id,
    name: data.name,
    comment: data.comment,
    description: data.description,
    origin_key: originKeyToServer(data.originKey),
    struct_key: originKeyToServer(data.structKey),
    owner_id: data.ownerId,
    owner_type: data.ownerType,
    inherited_from_composite_id: data.inheritedFromCompositeId,
    inherited_from_composite_name: data.inheritedFromCompositeName,
    is_overwrite: data.isOverwrite,
    agent_id: data.agentId,
    frame_id: data.frameId,
    source: data.source,
    qualification: data.qualification,
    local_plus: data.localPlus,
    state: data.state
  };
}

export interface KeyData {
  metaID?: string;
  ownerID?: string;
  frameID?: string;
  agentID?: string;
  ownerType?: string;
}

export function genKey(data: KeyData) {
  return {
    meta_id: data.metaID,
    owner_id: data.ownerID,
    frame_id: data.frameID,
    agent_id: data.agentID || getAgentId(),
    owner_type: data.ownerType
  };
}

export interface Tool {
  instance: string;
  method: string;
}

export interface AugmentMeta {
  type: 'augment_meta';
  prompt: string;
  modelId?: string; // Make modelId optional
  inferenceConfig?: InferenceConfig;
  contextCollections?: FilteredKnowledge[];
  contexts?: string[];
  examples?: string[];
  exampleCollections?: FilteredKnowledge[];
  structuredOutput: boolean;
  targetFrame?: string;
  targetSlot?: string;
}

export interface InferenceConfig {
  temperature: number;
  topk: number;
  maxInputLength: number;
  maxThinkLength: number;
  maxOutputLength: number;
}

export const defaultInferenceConfig: InferenceConfig = {
  temperature: 1,
  topk: 20,
  maxInputLength: 1024,
  maxThinkLength: 1024,
  maxOutputLength: 1024
};

export const defaultAugmentMeta: AugmentMeta = {
  type: 'augment_meta',
  prompt: '',
  modelId: undefined,
  inferenceConfig: defaultInferenceConfig,
  contextCollections: [],
  contexts: [],
  examples: [],
  exampleCollections: [],
  structuredOutput: false
};

export interface KnowledgeTag {
  key: string;
  value: string;
}

export interface FilteredKnowledge {
  remote?: boolean;
  source?: string;
  fileType?: string;
  knowledgeLabel?: string;
  relatedTags?: KnowledgeTag[];
}

export function augmentMetaToLocal(data: any) {
  return {
    prompt: data.prompt,
    modelId: data.model_id,
    inferenceConfig: data.inference_config && inferenceConfigToLocal(data.inference_config) || undefined,
    contextCollections: data.context_collections ? data.context_collections.map(filteredKnowledgeToLocal) : undefined,
    contexts: data.contexts,
    examples: data.examples,
    exampleCollections: data.example_collections ? data.example_collections.map(filteredKnowledgeToLocal) : undefined,
    structuredOutput: data.structured_output,
    targetFrame: data.target_frame,
    targetSlot: data.target_slot
  };
}

export function augmentMetaToServer(data: AugmentMeta) {
  return {
    prompt: data.prompt,
    model_id: data.modelId,
    inference_config: data.inferenceConfig ? inferenceConfigToServer(data.inferenceConfig) : undefined,
    context_collections: data.contextCollections ? data.contextCollections.map(filteredKnowledgeToServer) : undefined,
    contexts: data.contexts,
    examples: data.examples,
    example_collections: data.exampleCollections ? data.exampleCollections.map(filteredKnowledgeToServer) : undefined,
    structured_output: data.structuredOutput,
    target_frame: data.targetFrame,
    target_slot: data.targetSlot
  };
}

export function inferenceConfigToLocal(data: any) {
  return {
    temperature: data.temperature,
    topk: data.topk,
    maxInputLength: data.max_input_length,
    maxThinkLength: data.max_think_length,
    maxOutputLength: data.max_output_length
  };
}

export function inferenceConfigToServer(data: InferenceConfig) {
  return {
    temperature: data.temperature,
    topk: data.topk,
    max_input_length: data.maxInputLength,
    max_think_length: data.maxThinkLength,
    max_output_length: data.maxOutputLength
  };
}

export function filteredKnowledgeToLocal(data: any) {
  return {
    remote: data.remote,
    source: data.source,
    fileType: data.file_type,
    knowledgeLabel: data.knowledge_label,
    relatedTags: data.related_tags ? data.related_tags.map(knowledgeTagToLocal) : undefined
  };
}

export function filteredKnowledgeToServer(data: FilteredKnowledge) {
  return {
    remote: data.remote,
    source: data.source,
    file_type: data.fileType,
    knowledge_label: data.knowledgeLabel,
    related_tags: data.relatedTags ? data.relatedTags.map(knowledgeTagToServer) : undefined
  };
}

export function knowledgeTagToLocal(data: any) {
  return {
    key: data.key,
    value: data.value
  };
}

export function knowledgeTagToServer(data: KnowledgeTag) {
  return {
    key: data.key,
    value: data.value
  };
}
